import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Maximize2, Film } from "lucide-react";

const productions = [
  {
    id: "jd-red-valley",
    title: "Spot JD Italia x Red Valley Festival",
    tag: "Spot · Music & Fashion",
    year: "2026",
    src: "/assets/jd-red-valley.mp4",
    poster: "/assets/jd-red-valley-poster.jpg",
    desc: "Direzione creativa e produzione video per lo spot ufficiale della partnership tra JD Italia e Red Valley Festival: shooting on-stage, montaggio ritmato e color grading cinematografico.",
    credits: ["Concept & regia", "Riprese live durante il festival", "Editing & sound design", "Color grading"],
  },
];

export default function Produzione() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = productions[activeIdx];
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setError(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [activeIdx]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          setError(true);
          setPlaying(false);
        });
      }
    } else {
      v.pause();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const openFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <section id="produzione" data-testid="section-produzione" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-purple-300/80 mb-4 flex items-center gap-2">
            <Film size={12} /> 03 · Produzione contenuti
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
            Dallo storyboard <br /> alla <span className="text-gradient">post-produzione</span>.
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg">
            Realizziamo contenuti video ad alto impatto: spot, aftermovie, campagne social e
            documentari per brand, eventi e artisti.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 glass gradient-border rounded-3xl overflow-hidden group"
            data-testid={`prod-video-${active.id}`}
          >
            <div className="relative aspect-video bg-black">
              <video
                ref={videoRef}
                key={active.src}
                src={active.src}
                poster={active.poster}
                playsInline
                muted={muted}
                preload="metadata"
                onPlay={() => { setPlaying(true); setError(false); }}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onError={() => { setError(true); setPlaying(false); }}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="produzione-video-el"
              />
              {/* Gradient overlay */}
              <div
                className={`pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 transition-opacity duration-500 ${
                  playing ? "opacity-0" : "opacity-100"
                }`}
              />
              {/* Play overlay */}
              {!playing && !error && (
                <button
                  type="button"
                  onClick={togglePlay}
                  data-testid="produzione-play"
                  aria-label="Riproduci video"
                  className="absolute inset-0 flex items-center justify-center focus-visible:outline-none"
                >
                  <span className="relative flex items-center justify-center h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/10 border border-white/30 backdrop-blur-md shadow-[0_20px_60px_rgba(147,51,234,0.35)] group-hover:scale-105 transition-transform">
                    <span className="absolute inset-0 rounded-full animate-ping bg-white/10" />
                    <Play className="text-white ml-1" size={26} strokeWidth={1.7} fill="currentColor" />
                  </span>
                </button>
              )}
              {error && (
                <div
                  data-testid="produzione-error"
                  className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-3 bg-black/60 backdrop-blur-sm"
                >
                  <span className="text-white font-display text-lg">Impossibile riprodurre il video nel tuo browser.</span>
                  <a
                    href={active.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost text-xs py-2 px-4"
                    data-testid="produzione-open-external"
                  >
                    Apri in una nuova scheda
                  </a>
                </div>
              )}
              {/* Controls bar */}
              <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={playing ? "Pausa" : "Play"}
                  data-testid="produzione-toggle"
                  className="h-9 w-9 rounded-full bg-black/60 border border-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition"
                >
                  {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" fill="currentColor" />}
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={muted ? "Attiva audio" : "Disattiva audio"}
                  data-testid="produzione-mute"
                  className="h-9 w-9 rounded-full bg-black/60 border border-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition"
                >
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
                <button
                  type="button"
                  onClick={openFullscreen}
                  aria-label="Schermo intero"
                  data-testid="produzione-fullscreen"
                  className="ml-auto h-9 w-9 rounded-full bg-black/60 border border-white/20 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition"
                >
                  <Maximize2 size={14} />
                </button>
              </div>
              {/* Top tag */}
              <div className="absolute top-4 left-4 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3 py-1 text-[10px] tracking-widest uppercase text-white/90">
                {active.tag}
              </div>
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-4 glass gradient-border rounded-3xl p-7 md:p-8 flex flex-col"
          >
            <div className="text-xs uppercase tracking-widest text-purple-300/80">{active.year}</div>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-2 leading-tight">
              {active.title}
            </h3>
            <p className="mt-5 text-sm text-slate-300 leading-relaxed">{active.desc}</p>

            <div className="mt-7">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Credits</div>
              <ul className="space-y-2">
                {active.credits.map((c) => (
                  <li key={c} className="text-sm text-slate-100 flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gradient-to-r from-purple-400 to-blue-400" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            {productions.length > 1 && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Altre produzioni</div>
                <div className="flex flex-wrap gap-2">
                  {productions.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      data-testid={`prod-thumb-${p.id}`}
                      className={`text-xs rounded-full px-3 py-1.5 border transition ${
                        i === activeIdx
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 border-transparent text-white"
                          : "bg-white/[0.03] border-white/10 text-slate-300 hover:border-white/25"
                      }`}
                    >
                      {p.title.split(" ").slice(0, 3).join(" ")}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
